import apiClient from './axios';

export interface BroadcastNotification {
  id: string;
  sentById: string | null;
  sentBy: { firstName: string; lastName: string; email: string } | null;
  title: string;
  body: string;
  target: 'users' | 'vendors' | 'all';
  channels: ('push' | 'email')[];
  status: 'pending' | 'sending' | 'completed' | 'failed';
  totalRecipients: number;
  pushSent: number;
  pushFailed: number;
  emailSent: number;
  emailFailed: number;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface BroadcastAnalytics {
  totalBroadcasts: number;
  totalRecipients: number;
  totalPushSent: number;
  totalPushFailed: number;
  totalEmailSent: number;
  totalEmailFailed: number;
  byTarget: { target: string; count: number }[];
  recentBroadcasts: BroadcastNotification[];
}

export type AdminAlertType =
  | 'support_request'
  | 'support_overdue'
  | 'order_dispute'
  | 'product_request';
export type AdminAlertPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface AdminAlert {
  id: string;
  notificationId: string;
  type: AdminAlertType;
  priority: AdminAlertPriority;
  title: string;
  body: string;
  targetUrl: string;
  entityType: string;
  entityId: string;
  metadata: Record<string, unknown> | null;
  readAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
}

export const notificationsApi = {
  async sendBroadcast(data: {
    title: string;
    body: string;
    target: 'users' | 'vendors' | 'all';
    channels: ('push' | 'email')[];
  }): Promise<BroadcastNotification> {
    const res = await apiClient.post('/admin/notifications/broadcast', data);
    return res.data.data;
  },

  async listBroadcasts(params: {
    page?: number;
    limit?: number;
  }): Promise<{
    items: BroadcastNotification[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const res = await apiClient.get('/admin/notifications/broadcasts', { params });
    return res.data.data;
  },

  async getAnalytics(): Promise<BroadcastAnalytics> {
    const res = await apiClient.get('/admin/notifications/broadcasts/analytics');
    return res.data.data;
  },

  async getBroadcast(id: string): Promise<BroadcastNotification> {
    const res = await apiClient.get(`/admin/notifications/broadcasts/${id}`);
    return res.data.data;
  },

  async listInbox(params: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
  }): Promise<{
    items: AdminAlert[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const query = {
      page: params.page,
      limit: params.limit,
      ...(params.unreadOnly ? { unreadOnly: true } : {}),
    };
    const res = await apiClient.get('/admin/notifications/inbox', {
      params: query,
    });
    return res.data.data;
  },

  async getUnreadCount(): Promise<{ count: number }> {
    const res = await apiClient.get('/admin/notifications/inbox/unread-count');
    return res.data.data;
  },

  async markInboxRead(id: string): Promise<{ read: boolean }> {
    const res = await apiClient.patch(`/admin/notifications/inbox/${id}/read`);
    return res.data.data;
  },

  async markInboxAllRead(): Promise<{ read: boolean }> {
    const res = await apiClient.patch('/admin/notifications/inbox/read-all');
    return res.data.data;
  },
};
